import nProgress from 'nprogress';
import { setCookie } from 'utilities';

export type IdentityDto = {
  outcome?: number;
  token?: string;
  expires?: string;
  federated?: boolean;
  url?: string;
};

const API_ENV = process.env.REACT_APP_API_ENV || '';
const IDENTITY_ENV = process.env.REACT_APP_IDENTITY || '';

let renewPromise: Promise<string | null> | null = null;
let authRefreshInstalled = false;
let renewalTimer: number | null = null;
let renewalFailureCount = 0;
let logoutInProgress = false;

const AUTH_RETRY_FLAG = '__espAuthRetried';

const clearAuthStorage = () => {
  if (renewalTimer !== null) {
    window.clearTimeout(renewalTimer);
    renewalTimer = null;
  }

  sessionStorage.clear();
  localStorage.clear();
};

const parseJwtExpiry = (token: string): number | null => {
  try {
    const payload = token.split('.')[1];

    if (!payload) {
      return null;
    }

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    const decoded = JSON.parse(json) as { exp?: number };

    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

const getTokenExpiryTime = () => {
  const expires = localStorage.getItem('expires');
  if (expires) {
    const parsed = Date.parse(expires);

    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  const token = localStorage.getItem('token');
  if (!token) {
    return null;
  }

  return parseJwtExpiry(token);
};

const redirectToIdentitySignOut = () => {
  window.location.href = `${IDENTITY_ENV}/SignOut?ref=${window.location.origin}`;
};

const withAuthorization = (init: RequestInit = {}, accessToken?: string | null): RequestInit => {
  const headers = new Headers(init.headers);

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  } else {
    headers.delete('Authorization');
  }

  return {
    ...init,
    headers,
  };
};

export const authenticatedFetch = async (
  input: RequestInfo,
  init: RequestInit = {},
  retryOnUnauthorized = true
): Promise<Response> => {
  const currentToken = localStorage.getItem('token');
  const response = await fetch(input, withAuthorization(init, currentToken));

  if (response.status !== 401 || !retryOnUnauthorized || !currentToken) {
    return response;
  }

  const renewedToken = await renewSession();
  if (!renewedToken) {
    return response;
  }

  return fetch(input, withAuthorization(init, renewedToken));
};

export const installFetchAuthRefresh = () => {
  if (authRefreshInstalled) {
    return;
  }

  const nativeFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init: RequestInit = {}) => {
    const response = await nativeFetch(input, init);

    if (response.status !== 401) {
      return response;
    }

    const requestUrl = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const requestMethod = (init.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const renewEndpoint = `${API_ENV}/identity`;
    const isRenewCall = requestMethod === 'PATCH' && requestUrl.indexOf(renewEndpoint) === 0;
    const hasRetried = Boolean((init as any)[AUTH_RETRY_FLAG]);
    const currentToken = localStorage.getItem('token');

    if (isRenewCall || hasRetried || !currentToken) {
      return response;
    }

    const renewedToken = await renewSession();
    if (!renewedToken) {
      return response;
    }

    const retryInit = withAuthorization(init, renewedToken);
    const retryInitWithFlag = {
      ...retryInit,
      [AUTH_RETRY_FLAG]: true,
    } as RequestInit;

    return nativeFetch(input, retryInitWithFlag);
  };

  authRefreshInstalled = true;
};

export const checkFederated = async (email: string) => {
  try {
    const endpoint = `${API_ENV}/identity?email=${encodeURIComponent(email)}&host=${encodeURIComponent(
      window.location.origin
    )}`;
    const requestOptions = {
      method: 'GET',
    };
    const response = await fetch(endpoint, requestOptions);
    if (!response.ok) {
      throw Error();
    }
    const data = (await response.json()) as IdentityDto;
    if (data.federated) {
      return data.url;
    } else {
      return '';
    }
  } catch (error) {
    nProgress.done();
    return null;
  }
};

export const login = async (email: string, password: string) => {
  const states = {
    0: 'Sorry, those details are incorrect. Please try again.',
    1: 'Sorry, this account is locked. Please contact support.',
    2: 'Success! Redirecting you now.',
    3: 'MFA',
  };
  try {
    const endpoint = `${API_ENV}/identity`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    };
    const response = await fetch(endpoint, requestOptions);
    if (!response.ok) {
      throw Error();
    }
    const data = (await response.json()) as IdentityDto;
    const resultState = states[data.outcome];
    if (data.outcome === 2) {
      storeTokenData(data);
    }
    nProgress.done();
    return resultState;
  } catch (error) {
    nProgress.done();
    return states[1];
  }
};

export const queueRenewal = () => {
  if (renewalTimer !== null) {
    window.clearTimeout(renewalTimer);
    renewalTimer = null;
  }

  const expiryTime = getTokenExpiryTime();
  if (!expiryTime) {
    return;
  }

  const delay = Math.max(expiryTime - Date.now() - 1000, 0);

  renewalTimer = window.setTimeout(() => {
    renewalTimer = null;
    void renewSession().then((renewedToken) => {
      if (renewedToken) {
        renewalFailureCount = 0;
        queueRenewal();
      } else {
        renewalFailureCount += 1;

        const shouldRetryRenewal = document.hidden || !navigator.onLine || renewalFailureCount < 3;
        if (shouldRetryRenewal) {
          const retryDelayMs = Math.min(30000, 5000 * renewalFailureCount);
          renewalTimer = window.setTimeout(() => {
            renewalTimer = null;
            queueRenewal();
          }, retryDelayMs);
          return;
        }

        logout(true);
      }
    });
  }, delay);
};

export const renewSession = async (): Promise<string | null> => {
  const currentToken = localStorage.getItem('token');
  if (!currentToken) {
    return null;
  }

  if (renewPromise) {
    return renewPromise;
  }

  renewPromise = fetch(`${API_ENV}/identity`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${currentToken}`,
    },
  })
    .then(async (response) => {
      if (!response.ok) {
        throw Error();
      }

      const data = (await response.json()) as IdentityDto;
      if (data.outcome !== 2 || !data.token) {
        throw Error();
      }

      storeTokenData(data);
      queueRenewal();
      return data.token;
    })
    .catch(() => {
      return null;
    })
    .finally(() => {
      renewPromise = null;
    });

  return renewPromise;
};

export const logout = (sessionTimeout: boolean) => {
  if (logoutInProgress) {
    return;
  }

  logoutInProgress = true;
  const currentToken = localStorage.getItem('token');

  clearAuthStorage();

  const redirect = () => {
    if (sessionTimeout) {
      setTimeout(() => {
        redirectToIdentitySignOut();
      }, 0);
    } else {
      redirectToIdentitySignOut();
    }
  };

  if (!currentToken) {
    redirect();
    return;
  }

  void fetch(`${API_ENV}/identity/logout`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${currentToken}`,
    },
    keepalive: true,
  }).catch(() => {
    // Best-effort server logout; client-side logout already completed.
  });

  redirect();
};

export const forgotPassword = async (emailAddress: string) => {
  nProgress.start();
  try {
    const endpoint = `${API_ENV}/identity/forgot`;
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ emailAddress }),
    };
    const response = await fetch(endpoint, requestOptions);
    if (response.status === 204) {
      nProgress.done();
      return true;
    } else {
      throw Error();
    }
  } catch (error) {
    nProgress.done();
    return false;
  }
};

export const resetPassword = async (emailAddress: string, password: string, resetCode: string) => {
  nProgress.start();
  try {
    const endpoint = `${API_ENV}/identity/forgot`;
    const requestOptions = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ emailAddress, password, resetCode }),
    };
    const response = await fetch(endpoint, requestOptions);
    if (response.status === 204) {
      nProgress.done();
      return true;
    } else {
      throw Error();
    }
  } catch (error) {
    nProgress.done();
    return false;
  }
};

export const storeTokenData = (data: IdentityDto) => {
  if (data.token) {
    localStorage.setItem('token', data.token);
    setCookie('token', data.token);
  }

  if (data.expires) {
    localStorage.setItem('expires', data.expires);
  }

  queueRenewal();
};
